import Model, { attr, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import sumBy from 'lodash-es/sumBy';
import numeral from 'numeral';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { ReviewStatisticCategory } from 'uplisting-frontend/models/review-statistic-category';

export enum AirbnbNegativeTag {
  most_common_negative_tag = 'GUEST_REVIEW_HOST_NEGATIVE_SMELL',
}

export enum AirbnbPositiveTag {
  most_common_positive_tag = 'GUEST_REVIEW_HOST_POSITIVE_HAD_LISTED_AMENITIES_AND_SERVICES',
}

export default class ReviewStatisticModel extends Model {
  @attr('string') mostCommonPositiveTag?: AirbnbPositiveTag;
  @attr('string') mostCommonNegativeTag?: AirbnbNegativeTag;

  @hasMany('review-statistic-category', { inverse: null, async: false })
  categories!: ModelRegistry['review-statistic-category'][];

  @cached
  get accuracy(): string {
    return this.calculateAverageFor(ReviewStatisticCategory.accuracy);
  }

  @cached
  get overallRating(): string {
    return this.calculateAverageFor(ReviewStatisticCategory.overallRating);
  }

  @cached
  get value(): string {
    return this.calculateAverageFor(ReviewStatisticCategory.value);
  }

  @cached
  get cleanliness(): string {
    return this.calculateAverageFor(ReviewStatisticCategory.cleanliness);
  }

  @cached
  get checkIn(): string {
    return this.calculateAverageFor(ReviewStatisticCategory.checkIn);
  }

  @cached
  get communication(): string {
    return this.calculateAverageFor(ReviewStatisticCategory.communication);
  }

  @cached
  get location(): string {
    return this.calculateAverageFor(ReviewStatisticCategory.location);
  }

  private calculateAverageFor(category: ReviewStatisticCategory): string {
    const relevant = this.categories.filter(
      (item) => item.category === category && item.rating !== 0,
    );

    const overall = sumBy(relevant, 'rating') / relevant.length;

    return numeral(overall).format('0.00');
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'review-statistic': ReviewStatisticModel;
  }
}
